import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { isChildType } from "../util/isChildType";
import { KeyCode } from "../util/keyCodes";
import { Card } from "./Card/Card";

const Accordion = ({ activeKey, children, className }) => {
	const [selectedKey, setSelectedKey] = useState(activeKey);
	const [focusedKey, setFocusedKey] = useState(null);
	const [isFocused, setIsFocused] = useState(false);
	const setFocus = key => {
		setFocusedKey(key);
		setIsFocused(true);
	};

	const data = useMemo(() => {
		// Changing this to Effect will create flick after building it or production
		const cards = [];
		React.Children.forEach(children, child => {
			if (isChildType(child, Card)) {
				cards.push({
					id: child.props.eventKey,
					className: child.props.className,
					content: child.props.children,
					onToggleHandler: child?.props?.children?.[0]?.props?.onToggle,
					disabled: child?.props?.children?.[0]?.props?.isDisabled,
				});
			}
		});
		setFocusedKey(cards && cards[0]?.id);
		return cards;
	}, [children]);
	useEffect(() => {
		setSelectedKey(activeKey);
	}, [activeKey]);

	useEffect(() => {
		if (window.location.hash) {
			const hash = window.location.hash.substring(1);
			if (data.findIndex(item => item.id === hash) > -1) {
				setSelectedKey(hash);
				setFocus(hash);
			}
		}
	}, [data, activeKey]);
	const handleClick = (e, key, onToggle, disabled) => {
		e.preventDefault();

		if (onToggle) {
			onToggle(e, key);
		}

		if (disabled) {
			return;
		}

		if (selectedKey === key) {
			setSelectedKey(activeKey);
			setIsFocused(false);
		} else {
			setSelectedKey(key);
			setFocus(key);
		}
	};

	const handleKeyDown = (e, key) => {
		const length = data?.length;
		const index = data.findIndex(item => item.id === focusedKey);
		let targetIndex;
		if (e.keyCode === KeyCode.CODE_HOME) {
			targetIndex = 0;

			setFocus(data[targetIndex].id);
			e.preventDefault();
		} else if (e.keyCode === KeyCode.CODE_END) {
			targetIndex = length - 1;

			setFocus(data[targetIndex].id);
			e.preventDefault();
		} else if (e.keyCode === KeyCode.CODE_UP && index > 0) {
			targetIndex = index - 1;

			setFocus(data[targetIndex].id);
			e.preventDefault();
		} else if (e.keyCode === KeyCode.CODE_DOWN && index < length - 1) {
			targetIndex = index + 1;

			setFocus(data[targetIndex].id);
			e.preventDefault();
		} else if (e.keyCode === KeyCode.CODE_SPACE) {
			setSelectedKey(key);
			setFocus(key);
			e.preventDefault();
		}
	};

	return (
		<div className={className}>
			{data.map(child => (
				<Card
					id={child.id}
					key={child.id}
					selectedKey={selectedKey}
					focusedKey={focusedKey}
					onClick={event => handleClick(event, child.id, child.onToggleHandler, child.disabled)}
					onKeyDown={handleKeyDown}
					className={child.className}
					isFocused={isFocused}>
					{child.content}
				</Card>
			))}
		</div>
	);
};

Accordion.defaultProps = {
	className: "",
	activeKey: "",
};

Accordion.propTypes = {
	className: PropTypes.string,
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
	activeKey: PropTypes.string,
};

export default Accordion;
export { Accordion, Card };
